// Need to use the React-specific entry point to import createApi
import type {
  BulkActivateInstanceComplianceStandardsRequest,
  ComplianceStandardResponse,
  CustomComplianceStandardRequest,
  GetComplianceStandardsListRequestV2,
  GetInstanceComplianceStandardsListRequestV2,
  GetListResponseOfComplianceStandardResponse,
  GetListResponseOfSelectOption,
  GetListResponseOfSimpleInclusiveListResponse,
  InstanceComplianceStandardRequest,
  InstanceComplianceStandardResponse,
  TreeResponse,
} from 'src/api/ProactApiClient';
import {
  complianceStandardApiClient,
  instanceComplianceStandardApiClient,
} from 'src/api/ProactApiClientInstances';
import {complianceStandardSelectOptionsQueryFn} from 'src/app/tasks/offline/reads';
import {emptySplitApi} from '../api';
import {clientQuery} from '../clientQuery';

// Define a service using a base URL and expected endpoints
export const complianceStandardApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    // Global Admin endpoints
    getGlobalComplianceStandardListV2: builder.query<
      GetListResponseOfComplianceStandardResponse,
      GetComplianceStandardsListRequestV2
    >({
      queryFn: clientQuery(params =>
        complianceStandardApiClient.listComplianceStandardV2(params)
      ),
      providesTags: ['ComplianceStandards'],
    }),
    getGlobalComplianceStandard: builder.query<
      ComplianceStandardResponse,
      number
    >({
      queryFn: clientQuery(id =>
        complianceStandardApiClient.getComplianceStandard(id)
      ),
      providesTags: ['ComplianceStandards'],
    }),
    // Instance-scoped endpoints
    getComplianceStandardSelectOptions: builder.query<
      GetListResponseOfSelectOption,
      void
    >({
      queryFn: complianceStandardSelectOptionsQueryFn(() =>
        instanceComplianceStandardApiClient.listSelectOptions({
          length: 10000,
          start: 0,
          active: true,
        })
      ),
      providesTags: ['ComplianceStandards'],
    }),
    getSimpleComplianceStandardList: builder.query<
      GetListResponseOfSimpleInclusiveListResponse,
      void
    >({
      queryFn: clientQuery(() =>
        instanceComplianceStandardApiClient.listSimpleComplianceStandard({
          length: 10000,
          start: 0,
          active: true,
        })
      ),
      providesTags: ['ComplianceStandards'],
    }),
    getComplianceStandardListV2: builder.query<
      GetListResponseOfComplianceStandardResponse,
      GetInstanceComplianceStandardsListRequestV2
    >({
      queryFn: clientQuery(params =>
        instanceComplianceStandardApiClient.listComplianceStandardV2(params)
      ),
      providesTags: ['ComplianceStandards', 'InstanceComplianceStandards'],
    }),
    getCustomComplianceStandardListV2: builder.query<
      GetListResponseOfComplianceStandardResponse,
      GetComplianceStandardsListRequestV2
    >({
      queryFn: clientQuery(params =>
        complianceStandardApiClient.listCustomComplianceStandardV2(params)
      ),
      providesTags: ['ComplianceStandards'],
    }),
    getCustomComplianceStandardTree: builder.query<
      TreeResponse[],
      GetComplianceStandardsListRequestV2
    >({
      queryFn: clientQuery(params =>
        complianceStandardApiClient.listCustomComplianceStandardTree(params)
      ),
      providesTags: ['ComplianceStandards'],
    }),
    getComplianceStandardTree: builder.query<
      TreeResponse[],
      GetInstanceComplianceStandardsListRequestV2
    >({
      queryFn: clientQuery(params =>
        instanceComplianceStandardApiClient.listComplianceStandardTree(params)
      ),
      providesTags: ['ComplianceStandards', 'InstanceComplianceStandards'],
    }),
    getCustomComplianceStandard: builder.query<
      ComplianceStandardResponse,
      number
    >({
      queryFn: clientQuery(id =>
        complianceStandardApiClient.getCustomComplianceStandard(id)
      ),
      providesTags: ['ComplianceStandards'],
    }),
    createCustomComplianceStandard: builder.mutation<
      ComplianceStandardResponse,
      CustomComplianceStandardRequest
    >({
      queryFn: clientQuery(request =>
        complianceStandardApiClient.createCustomComplianceStandard(request)
      ),
      invalidatesTags: ['ComplianceStandards'],
    }),
    updateCustomComplianceStandard: builder.mutation<
      ComplianceStandardResponse,
      CustomComplianceStandardRequest
    >({
      queryFn: clientQuery(request =>
        complianceStandardApiClient.updateCustomComplianceStandard(request)
      ),
      invalidatesTags: ['ComplianceStandards'],
    }),
    // Instance compliance standard endpoints
    getInstanceComplianceStandard: builder.query<
      InstanceComplianceStandardResponse,
      number
    >({
      queryFn: clientQuery(id =>
        instanceComplianceStandardApiClient.getComplianceStandard(id)
      ),
      providesTags: ['InstanceComplianceStandards'],
    }),
    createInstanceComplianceStandard: builder.mutation<
      InstanceComplianceStandardResponse,
      InstanceComplianceStandardRequest
    >({
      queryFn: clientQuery(request =>
        instanceComplianceStandardApiClient.createComplianceStandard(request)
      ),
      invalidatesTags: ['InstanceComplianceStandards'],
    }),
    updateInstanceComplianceStandard: builder.mutation<
      InstanceComplianceStandardResponse,
      InstanceComplianceStandardRequest
    >({
      queryFn: clientQuery(request =>
        instanceComplianceStandardApiClient.updateComplianceStandard(request)
      ),
      invalidatesTags: ['InstanceComplianceStandards'],
    }),
    bulkActivateInstanceComplianceStandards: builder.mutation<
      number,
      BulkActivateInstanceComplianceStandardsRequest
    >({
      queryFn: clientQuery(request =>
        instanceComplianceStandardApiClient.bulkActivateComplianceStandard(
          request
        )
      ),
      invalidatesTags: ['InstanceComplianceStandards'],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetGlobalComplianceStandardListV2Query,
  useGetGlobalComplianceStandardQuery,
  useGetSimpleComplianceStandardListQuery,
  useGetComplianceStandardSelectOptionsQuery,
  useGetComplianceStandardListV2Query,
  useGetCustomComplianceStandardListV2Query,
  useGetCustomComplianceStandardTreeQuery,
  useGetComplianceStandardTreeQuery,
  useGetCustomComplianceStandardQuery,
  useCreateCustomComplianceStandardMutation,
  useUpdateCustomComplianceStandardMutation,
  useGetInstanceComplianceStandardQuery,
  useCreateInstanceComplianceStandardMutation,
  useUpdateInstanceComplianceStandardMutation,
  useBulkActivateInstanceComplianceStandardsMutation,
} = complianceStandardApi;
